import { assetPath } from "./assetPath";

/**
 * Centralized mapping of page/context keys to their downloadable PDF paths.
 * PDFs live in public/CTA/downloadDoc/.
 * Each page that has a "Download" CTA references a key here.
 */
const RAW_MAP = {
  // Platform pages
  raptor: "/CTA/downloadDoc/AEPL-RPTR-DS-001_RAPTOR_Platform_Datasheet.pdf",
  griffin: "/CTA/downloadDoc/AEPL-GRFN-DS-001_GRIFFIN_XR_Datasheet.pdf",
  aurora: "/CTA/downloadDoc/AEPL-AURA-IB-001_Aurora_Intelligence_Brief.pdf",
  learnon: "/CTA/downloadDoc/AEPL-LRNO-TB-001_LearnOn_Technical_Brief.pdf",
  courseai: "/CTA/downloadDoc/AEPL-CRSI-TB-001_CourseAI_Technical_Brief.pdf",

  // Industry hub pages
  "aviation-hero":
    "/CTA/downloadDoc/AEPL-AVI-IG-001_EBT_Implementation_Guide.pdf",
  "aviation-cta":
    "/CTA/downloadDoc/AEPL-AVI-TB-EASA-001_EASA_EBT_Technical_Brief.pdf",
  "defence-hero": "/CTA/downloadDoc/AEPL-DEF-PB-001_Defence_Platform_Brief.pdf",
  "defence-cta": "/CTA/downloadDoc/AEPL-DEF-PB-001_Defence_Platform_Brief.pdf",
  "energy-hero": "/CTA/downloadDoc/AEPL-ENG-CB-001_Energy_Competency_Brief.pdf",
  "energy-cta": "/CTA/downloadDoc/AEPL-ENG-CB-001_Energy_Competency_Brief.pdf",

  // Sub-pages (specific industry PDFs)
  "flight-deck-hero":
    "/CTA/downloadDoc/AEPL-AVI-PG-BASE-001_Baseline_Programme_Guide.pdf",
  "flight-deck-cta":
    "/CTA/downloadDoc/AEPL-AVI-TB-EBT-001_EBT_Technical_Brief.pdf",
  engineers:
    "/CTA/downloadDoc/AEPL-AVI-CB-147-001_Part147_Compliance_Brief.pdf",
  "cabin-crew-hero":
    "/CTA/downloadDoc/AEPL-AVI-CB-CC-001_Cabin_Crew_Compliance_Brief.pdf",
  "cabin-crew-cta":
    "/CTA/downloadDoc/AEPL-AVI-CB-SEP-001_SEP_Compliance_Management_Brief.pdf",

  // Home page
  home: "/CTA/downloadDoc/AEPL-PLAT-OV-001_Platform_Overview.pdf",
};

/**
 * Get the asset-path-resolved download URL for a given context key.
 * @param {string} key - One of the keys in RAW_MAP
 * @returns {string} Full path to the downloadable PDF
 */
export const getDownloadPath = (key) => {
  const path = RAW_MAP[key] || RAW_MAP.home;
  return assetPath(path);
};

/**
 * Build a unique filename with a timestamp suffix so re-downloads
 * of the same PDF don't silently overwrite each other.
 * e.g. "AEPL-RPTR-DS-001_RAPTOR_Platform_Datasheet_20260515_1830.pdf"
 */
const buildFilename = (url) => {
  const raw = url.substring(url.lastIndexOf("/") + 1);
  const ext = raw.substring(raw.lastIndexOf("."));
  const base = raw.substring(0, raw.lastIndexOf("."));
  const now = new Date();
  const ts =
    `${now.getFullYear()}` +
    `${String(now.getMonth() + 1).padStart(2, "0")}` +
    `${String(now.getDate()).padStart(2, "0")}_` +
    `${String(now.getHours()).padStart(2, "0")}` +
    `${String(now.getMinutes()).padStart(2, "0")}`;
  return `${base}_${ts}${ext}`;
};

/**
 * Pre-fetch the PDF blob so it's ready when we need to trigger the download.
 * Call this EARLY (before async form submission) to capture user-gesture context.
 *
 * @param {string} key - One of the keys in RAW_MAP
 * @returns {Promise<{blob: Blob, filename: string, url: string}>}
 */
export const prefetchDownload = async (key) => {
  const url = getDownloadPath(key);
  const filename = buildFilename(url);

  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const arrayBuffer = await res.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: "application/pdf" });
    return { blob, filename, url };
  } catch (err) {
    console.warn("Prefetch failed, will use direct URL:", err);
    return { blob: null, filename, url };
  }
};

/**
 * Execute a download from a prefetched result or a direct URL.
 *
 * @param {{blob: Blob|null, filename: string, url: string}} prefetched
 */
export const triggerPrefetchedDownload = (prefetched) => {
  const { blob, filename, url } = prefetched;

  const link = document.createElement("a");

  if (blob) {
    const blobUrl = URL.createObjectURL(blob);
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  } else {
    // Fallback: direct same-origin download
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * One-shot download trigger (for standalone use outside forms).
 * @param {string} key - One of the keys in RAW_MAP
 */
export const triggerDownload = async (key) => {
  const prefetched = await prefetchDownload(key);
  triggerPrefetchedDownload(prefetched);
};
